import { Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EscalationsTable } from "./EscalationsTable";
import { LeadsTable } from "./LeadsTable";
import { RecordFilters } from "./RecordFilters";
import { SessionsTable } from "./SessionsTable";

/** "Nothing here" wording depends on which list is empty and why. */
const emptyMessage = (tab, escalationState) => {
  if (tab === "fallbacks" && escalationState === "open") {
    return "Nobody is waiting on us right now.";
  }
  if (tab === "fallbacks" && escalationState === "overdue") {
    return "Nobody has been left waiting past the promise. Well done.";
  }
  return "No records match those filters yet.";
};

/**
 * The right table for the tab, or the right stand-in for it.
 *
 * Written as early returns rather than a chain of ternaries, because there are
 * five possible outcomes here and a reader should be able to find the one they
 * care about without counting brackets.
 */
const RecordTable = ({
  tab,
  rows,
  loading,
  emptyState,
  savingReference,
  onOpenSession,
  onCompare,
  onOpenFinance,
  onToggleHandled,
  team,
  savingOwner,
  onAssignOwner,
  onAddTeamMember,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading records…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div
        data-testid="admin-empty-state"
        className="py-16 text-center text-sm text-muted-foreground"
      >
        {emptyState}
      </div>
    );
  }
  if (tab === "sessions") return <SessionsTable rows={rows} onOpenSession={onOpenSession} />;
  if (tab === "leads") {
    return (
      <LeadsTable
        rows={rows}
        onOpenSession={onOpenSession}
        onCompare={onCompare}
        onOpenFinance={onOpenFinance}
      />
    );
  }
  return (
    <EscalationsTable
      rows={rows}
      savingReference={savingReference}
      onToggleHandled={onToggleHandled}
      team={team}
      savingOwner={savingOwner}
      onAssignOwner={onAssignOwner}
      onAddTeamMember={onAddTeamMember}
    />
  );
};

/**
 * The tabbed record list: sessions, leads, or the people waiting on us.
 *
 * The three tables are separate components, so this file only decides which
 * one is on screen and what the row count line above it says.
 */
export const RecordsPanel = ({
  tab,
  onTabChange,
  filters,
  rows,
  total,
  loading,
  escalationCounts,
  responseClock,
  savingReference,
  onExport,
  onOpenSession,
  onCompare,
  onOpenFinance,
  onToggleHandled,
  team,
  savingOwner,
  onAssignOwner,
  onAddTeamMember,
}) => (
  <div className="tt-card mt-6 overflow-hidden">
    <div className="flex flex-col gap-4 border-b border-border p-4 lg:flex-row lg:items-center lg:justify-between">
      <Tabs value={tab} onValueChange={onTabChange} className="max-w-full overflow-x-auto">
        <TabsList data-testid="admin-tabs" className="w-max">
          <TabsTrigger value="sessions" data-testid="admin-tab-sessions">
            All sessions
          </TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads">
            Leads &amp; quotes
          </TabsTrigger>
          <TabsTrigger value="fallbacks" data-testid="admin-tab-fallbacks">
            Needs a human
            {escalationCounts.open ? (
              <span
                data-testid="admin-tab-escalations-count"
                className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                  escalationCounts.overdue
                    ? "bg-destructive/10 text-destructive"
                    : "bg-[color:var(--tt-warn-bg)] text-[color:var(--tt-warn-text)]"
                }`}
              >
                {escalationCounts.open}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <RecordFilters tab={tab} filters={filters} onExport={onExport} />
    </div>

    <div className="px-4 py-2.5 text-xs text-muted-foreground">
      {loading ? "Loading…" : `${total} record${total === 1 ? "" : "s"}`}
      {rows.length < total ? ` · showing the most recent ${rows.length}` : ""}
      {tab === "fallbacks" && responseClock?.label ? (
        <span data-testid="admin-escalation-sla-note">
          {" · "}
          {responseClock.label} ({responseClock.business_day})
          {escalationCounts.overdue ? ` · ${escalationCounts.overdue} overdue` : ""}
        </span>
      ) : null}
    </div>

    <div className="overflow-x-auto">
      <RecordTable
        tab={tab}
        rows={rows}
        loading={loading}
        emptyState={emptyMessage(tab, filters.escalationState)}
        savingReference={savingReference}
        onOpenSession={onOpenSession}
        onCompare={onCompare}
        onOpenFinance={onOpenFinance}
        onToggleHandled={onToggleHandled}
        team={team}
        savingOwner={savingOwner}
        onAssignOwner={onAssignOwner}
        onAddTeamMember={onAddTeamMember}
      />
    </div>
  </div>
);
